import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Typography, Paper, Box, Button, List, ListItem, ListItemText, ListItemIcon,
  Dialog, DialogTitle, DialogContent, TextField, DialogActions, Table, 
  TableBody, TableCell, TableHead, TableRow, TableContainer, Tabs, Tab, Grid, Card, CardContent, CardActions,
  Select, MenuItem, FormControl, InputLabel, IconButton, LinearProgress, Checkbox,
  Accordion, AccordionSummary, AccordionDetails, Chip, Avatar, Divider, Tooltip, CircularProgress, Autocomplete
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DownloadIcon from '@mui/icons-material/Download';
import QrCodeIcon from '@mui/icons-material/QrCode';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import EventIcon from '@mui/icons-material/Event';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import MapIcon from '@mui/icons-material/Map';
import PublicIcon from '@mui/icons-material/Public';
import BusinessIcon from '@mui/icons-material/Business';
import FolderIcon from '@mui/icons-material/Folder';
import FlagIcon from '@mui/icons-material/Flag';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DescriptionIcon from '@mui/icons-material/Description';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  
  // Edit Mode State
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  // Team State
  const [team, setTeam] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignUser, setAssignUser] = useState('');
  const [assignRole, setAssignRole] = useState('Member');

  // Report State
  const [openReportDialog, setOpenReportDialog] = useState(false);
  const [newReportName, setNewReportName] = useState('');
  const [createReportLoading, setCreateReportLoading] = useState(false);

  // Documents State
  const [documents, setDocuments] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(false);

  // Member Details State
  const [openMemberDialog, setOpenMemberDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberHours, setMemberHours] = useState([]);

  // Milestones State
  const [milestones, setMilestones] = useState([]);
  const [openMilestoneDialog, setOpenMilestoneDialog] = useState(false);
  const [newMilestoneName, setNewMilestoneName] = useState('');
  const [newMilestoneDate, setNewMilestoneDate] = useState('');
  const [assignLoading, setAssignLoading] = useState(false);

  // QR Dialog State
  const [qrDialog, setQrDialog] = useState({ open: false, url: '', title: '' });

  // SubReport Upload State
  const [uploadSubReportId, setUploadSubReportId] = useState(null);
  const [uploadingSubReportId, setUploadingSubReportId] = useState(null);
  const [addingRevisionId, setAddingRevisionId] = useState(null);
  const fileInputRef = React.useRef(null);

  const token = localStorage.getItem('token');

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProject(res.data);
      setEditData(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeam = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/team`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeam(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/setup/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(res.data);
    } catch (err) {
      console.error("Error fetching users:", err);
    }
  };

  const fetchDocuments = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchMilestones = async () => {
    try {
      const res = await axios.get(`${API_URL}/projects/${id}/milestones`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMilestones(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProject();
    fetchTeam();
    fetchAllUsers();
    fetchDocuments();
    fetchMilestones();
  }, [id]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // --- Edit Project ---
  const handleSaveProject = async () => {
    try {
      await axios.put(`${API_URL}/projects/${id}`, editData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEditMode(false);
      fetchProject();
    } catch (err) {
      console.error(err);
      alert("Error updating project");
    }
  };

  const getLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setEditData({
            ...editData,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Error getting location. Please ensure location services are enabled.");
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleInputChange = (e) => {
    setEditData({ ...editData, [e.target.name]: e.target.value });
  };

  // --- Team Management ---
  const handleAssignUser = async () => {
    setAssignLoading(true);
    try {
      await axios.post(`${API_URL}/projects/${id}/team`, {
        user_id: assignUser,
        role: assignRole
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenAssignDialog(false);
      fetchTeam();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Error assigning user");
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveUser = async (userId) => {
    if(!window.confirm("Are you sure you want to remove this user?")) return;
    try {
      await axios.delete(`${API_URL}/projects/${id}/team?user_id=${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTeam();
    } catch (err) {
      console.error(err);
      alert("Error removing user");
    }
  };

  // --- Reports ---
  const handleAddReport = async () => {
    setCreateReportLoading(true);
    try {
      await axios.post(`${API_URL}/projects/${id}/reports`, { name: newReportName }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenReportDialog(false);
      setNewReportName('');
      fetchProject(); 
    } catch (err) {
      console.error(err);
    } finally {
      setCreateReportLoading(false);
    }
  };

  const handleAddSubReport = async (reportId) => {
    setAddingRevisionId(reportId);
    try {
        await axios.post(`${API_URL}/reports/${reportId}/subreports`, {}, {
            headers: { Authorization: `Bearer ${token}` }
        });
        await fetchProject();
    } catch (err) {
        console.error(err);
        alert("Error creating subreport");
    } finally {
        setAddingRevisionId(null);
    }
  };

  // --- Documents ---
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFile(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await axios.post(`${API_URL}/projects/${id}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      fetchDocuments();
    } catch (err) {
      console.error(err);
      alert("Error uploading file");
    } finally {
      setUploadingFile(false);
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) return;
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      await axios.post(`${API_URL}/projects/${id}/documents`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadFile(null);
      fetchDocuments();
    } catch (err) {
      console.error(err);
      alert("Error uploading file");
    }
  };

  // --- Milestones ---
  const handleAddMilestone = async () => {
    try {
      await axios.post(`${API_URL}/projects/${id}/milestones`, {
        name: newMilestoneName,
        due_date: newMilestoneDate
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOpenMilestoneDialog(false);
      setNewMilestoneName('');
      setNewMilestoneDate('');
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error creating milestone");
    }
  };

  const handleUpdateMilestoneStatus = async (milestoneId, status) => {
    const isCompleted = status === 'Completed';
    try {
      await axios.put(`${API_URL}/milestones/${milestoneId}`, {
        completed: isCompleted
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error updating milestone");
    }
  };

  const handleToggleMilestone = async (milestoneId, currentStatus) => {
    try {
      await axios.put(`${API_URL}/milestones/${milestoneId}`, {
        completed: !currentStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error updating milestone");
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if(!window.confirm("Delete this milestone?")) return;
    try {
      await axios.delete(`${API_URL}/milestones/${milestoneId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchMilestones();
    } catch (err) {
      console.error(err);
      alert("Error deleting milestone");
    }
  };

  const handleViewQR = (url, title) => {
    setQrDialog({ open: true, url, title });
  };

  const handleMemberClick = async (member) => {
    setSelectedMember(member);
    try {
        const res = await axios.get(`${API_URL}/projects/${id}/team/${member.user_id}/hours`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        setMemberHours(res.data);
        setOpenMemberDialog(true);
    } catch (err) {
        console.error(err);
        alert("Failed to fetch member hours");
    }
  };

  const handleUploadClick = (subReportId) => {
    setUploadSubReportId(subReportId);
    if (fileInputRef.current) {
        fileInputRef.current.click();
    }
  };

  const handleSubReportFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file || !uploadSubReportId) return;
    
    const currentUploadId = uploadSubReportId;
    setUploadingSubReportId(currentUploadId);

    const formData = new FormData();
    formData.append('file', file);

    try {
        await axios.post(`${API_URL}/subreports/${currentUploadId}/upload`, formData, {
            headers: { 
                Authorization: `Bearer ${token}`,
                'Content-Type': 'multipart/form-data'
            }
        });
        // Reset and refresh
        setUploadSubReportId(null);
        event.target.value = null; // Reset file input
        await fetchProject();
        alert("File uploaded successfully");
    } catch (err) {
        console.error(err);
        alert("Error uploading file");
    } finally {
        setUploadingSubReportId(null);
    }
  };

  const handleDownloadSubReport = (subReportId) => {
      const url = `${API_URL}/subreports/${subReportId}/download`;
      window.open(url, '_blank');
  };

  const handleDownloadDocument = (filename) => {
      const encodedFilename = encodeURIComponent(filename);
      const url = `${API_URL}/projects/${id}/documents/${encodedFilename}`;
      window.open(url, '_blank');
  };

  if (loading || !project) return <Loading message="Loading Project Details..." />;

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ 
        p: { xs: 2.5, md: 4 }, 
        mb: 4, 
        borderRadius: 4, 
        background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)', 
        color: 'white',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Decorative Circles */}
        <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', bgcolor: 'rgba(255, 255, 255, 0.05)' }} />
        <Box sx={{ position: 'absolute', bottom: -30, left: 100, width: 100, height: 100, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                    <Button 
                        startIcon={<ArrowBackIcon />} 
                        onClick={() => navigate('/projects')} 
                        sx={{ color: 'rgba(255,255,255,0.8)', mb: 2, pl: 0, '&:hover': { color: 'white', bgcolor: 'transparent' } }}
                    >
                        Back to Projects
                    </Button>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, mb: 1 }}>
                        <Typography variant="h3" component="h1" fontWeight="800" sx={{ letterSpacing: -1, fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' } }}>
                        {project.name}
                        </Typography>
                        <Chip 
                            label={project.status} 
                            sx={{ 
                                bgcolor: project.status === 'Completed' ? '#4caf50' : 'white', 
                                color: project.status === 'Completed' ? 'white' : 'primary.main', 
                                fontWeight: 'bold',
                                height: 28,
                                boxShadow: 2
                            }} 
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, md: 3 }, flexWrap: 'wrap', opacity: 0.9, mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight="500">Ref: {project.ref}</Typography>
                        {/* Divider */}
                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'block' } }} />
                        
                        {project.country && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <PublicIcon fontSize="small" />
                                <Typography variant="subtitle1">{project.country}</Typography>
                            </Box>
                        )}
                        
                        {project.division && (
                            <>
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'white', display: { xs: 'none', sm: 'block' } }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <BusinessIcon fontSize="small" />
                                    <Typography variant="subtitle1">{project.division}</Typography>
                                </Box>
                            </>
                        )}
                    </Box>
                    
                    <Box sx={{ mt: 3 }}>
                       <Button 
                           variant="contained" 
                           sx={{ 
                               bgcolor: 'rgba(255,255,255,0.2)', 
                               backdropFilter: 'blur(10px)',
                               color: 'white', 
                               boxShadow: 'none',
                               '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                               width: { xs: '100%', sm: 'auto' }
                           }}
                           onClick={() => editMode ? handleSaveProject() : setEditMode(true)}
                           startIcon={editMode ? <SaveIcon /> : <EditIcon />}
                       >
                           {editMode ? "Save Changes" : "Edit Project"}
                       </Button>
                    </Box>
                </Box>

                {/* QR Code Box */}
                <Box sx={{ 
                    bgcolor: 'white', 
                    p: 1.5, 
                    borderRadius: 2, 
                    boxShadow: 3,
                    textAlign: 'center',
                    alignSelf: { xs: 'center', md: 'flex-start' },
                    mb: { xs: 2, md: 0 }
                }}>
                    <img 
                        src={`${API_URL}/projects/${id}/qr`} 
                        alt="QR Code" 
                        style={{ width: 100, height: 100, display: 'block' }} 
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', mt: 0.5, display: 'block', fontWeight: 'bold' }}>
                        SCAN ME
                    </Typography>
                </Box>
            </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left Column: Main Content */}
        <Grid item xs={12}>
          <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2 }}
            >
              <Tab icon={<AssignmentIcon />} label="Overview" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<PersonAddIcon />} label="Team" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<InsertDriveFileIcon />} label="Reports" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<CloudUploadIcon />} label="Documents" iconPosition="start" sx={{ minHeight: 64 }} />
              <Tab icon={<EventIcon />} label="Milestones" iconPosition="start" sx={{ minHeight: 64 }} />
            </Tabs>

            {/* Overview Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Description Section */}
                <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" fontWeight="bold" gutterBottom color="primary">Project Description</Typography>
                  {editMode ? (
                    <TextField 
                      fullWidth multiline rows={4} 
                      name="description" 
                      value={editData.description} 
                      onChange={handleInputChange} 
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {project.description || "No description provided for this project."}
                    </Typography>
                  )}
                </Paper>

                {/* Info Cards Grid */}
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <BusinessIcon color="action" />
                            <Typography variant="h6" fontWeight="medium">Client Details</Typography>
                        </Box>
                        
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Client Name</Typography>
                            <Typography variant="h6" fontWeight="bold" gutterBottom>{project.client || 'N/A'}</Typography>
                            
                            <Divider sx={{ my: 1.5 }} />
                            
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body2" color="text.secondary">Quote Reference</Typography>
                                {editMode ? (
                                <TextField size="small" name="quote_ref" value={editData.quote_ref || ''} onChange={handleInputChange} />
                                ) : (
                                <Chip label={project.quote_ref || 'N/A'} size="small" sx={{ borderRadius: 1 }} />
                                )}
                            </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <AssignmentIcon color="action" />
                            <Typography variant="h6" fontWeight="medium">Project Health</Typography>
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>Current Status</Typography>
                                    {editMode ? (
                                        <Select size="small" fullWidth name="status" value={editData.status} onChange={handleInputChange}>
                                        <MenuItem value="Active">Active</MenuItem>
                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                        <MenuItem value="Bid Stage">Bid Stage</MenuItem>
                                        <MenuItem value="At Risk">At Risk</MenuItem>
                                        <MenuItem value="On Hold">On Hold</MenuItem>
                                        <MenuItem value="Submitted">Submitted</MenuItem>
                                        <MenuItem value="Under Review">Under Review</MenuItem>
                                        <MenuItem value="Overdue">Overdue</MenuItem>
                                        <MenuItem value="Completed">Completed</MenuItem>
                                        </Select>
                                    ) : (
                                        <Chip 
                                            label={project.status} 
                                            color={project.status === 'Completed' ? 'success' : project.status === 'Overdue' ? 'error' : 'primary'} 
                                            variant="filled" 
                                        />
                                    )}
                                </Box>
                            </Grid>
                            <Grid item xs={6}>
                                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                                    <Typography variant="caption" color="text.secondary" display="block" mb={1}>Priority Level</Typography>
                                    {editMode ? (
                                        <Select size="small" fullWidth name="priority" value={editData.priority} onChange={handleInputChange}>
                                        <MenuItem value="Low">Low</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="High">High</MenuItem>
                                        </Select>
                                    ) : (
                                        <Chip 
                                            label={project.priority} 
                                            color={project.priority === 'High' ? 'error' : project.priority === 'Low' ? 'success' : 'warning'} 
                                            variant="outlined" 
                                        />
                                    )}
                                </Box>
                            </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Progress Tracking */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <FlagIcon color="action" />
                            <Typography variant="h6" fontWeight="medium">Progress Tracking</Typography>
                        </Box>
                        <Box sx={{ mt: 2, mb: 3 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" fontWeight="medium">Completion</Typography>
                            <Typography variant="body2" color="primary.main" fontWeight="bold">{Math.round(project.progress)}%</Typography>
                          </Box>
                          <LinearProgress 
                            variant="determinate" 
                            value={Number(project.progress)} 
                            sx={{ height: 10, borderRadius: 5, bgcolor: 'grey.100', '& .MuiLinearProgress-bar': { borderRadius: 5 } }} 
                          />
                        </Box>
                        {editMode && (
                          <TextField 
                            fullWidth type="number" label="Update Progress %" name="progress" 
                            value={editData.progress} onChange={handleInputChange} size="small" 
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Key Dates */}
                  <Grid item xs={12} md={6}>
                    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <EventIcon color="action" />
                            <Typography variant="h6" fontWeight="medium">Key Dates</Typography>
                        </Box>
                        <List disablePadding>
                          <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <ListItemIcon sx={{ minWidth: 40 }}><EventIcon color="action" fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary={<Typography variant="body2" color="text.secondary">Start Date</Typography>}
                              secondary={<Typography variant="body1" fontWeight="medium">{project.start_date || "Not set"}</Typography>} 
                            />
                          </ListItem>
                          <ListItem sx={{ px: 0, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <ListItemIcon sx={{ minWidth: 40 }}><EventIcon color="action" fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary={<Typography variant="body2" color="text.secondary">Due Date</Typography>}
                              secondary={<Typography variant="body1" fontWeight="medium">{project.due_date || "Not set"}</Typography>} 
                            />
                          </ListItem>
                          <ListItem sx={{ px: 0, py: 1.5 }}>
                            <ListItemIcon sx={{ minWidth: 40 }}><AccessTimeIcon color="action" fontSize="small" /></ListItemIcon>
                            <ListItemText 
                              primary={<Typography variant="body2" color="text.secondary">Created On</Typography>}
                              secondary={<Typography variant="body1" fontWeight="medium">{project.created_at}</Typography>} 
                            />
                          </ListItem>
                        </List>
                        {editMode && (
                          <>
                          <TextField 
                            fullWidth type="date" label="Start Date" name="start_date" 
                            value={editData.start_date || ''} onChange={handleInputChange} InputLabelProps={{ shrink: true }} size="small" sx={{ mt: 2 }} 
                          />
                          <TextField 
                            fullWidth type="date" label="Due Date" name="due_date" 
                            value={editData.due_date || ''} onChange={handleInputChange} InputLabelProps={{ shrink: true }} size="small" sx={{ mt: 2 }} 
                          />
                          </>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12}>
                    <Card elevation={0} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                              <AttachMoneyIcon color="action" />
                              <Typography variant="h6" fontWeight="medium">Financial Overview</Typography>
                              <Box sx={{ flexGrow: 1 }} />
                              <Chip 
                                label={`${Math.round((project.spent / project.budget) * 100) || 0}% Utilized`} 
                                size="small" 
                                color={project.spent > project.budget ? 'error' : 'default'} 
                              />
                          </Box>
                          
                          <Grid container spacing={4} alignItems="center">
                              <Grid item xs={12} md={5}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.50', borderRadius: 2 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="bold">TOTAL BUDGET</Typography>
                                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                                          <Typography variant="h4" fontWeight="bold" color="primary.main">$</Typography>
                                          {editMode ? (
                                              <TextField variant="standard" type="number" name="budget" value={editData.budget} onChange={handleInputChange} sx={{ width: 100, input: { textAlign: 'center', fontSize: '1.5rem' } }} />
                                          ) : (
                                              <Typography variant="h4" fontWeight="bold" color="primary.main">{project.budget?.toLocaleString()}</Typography>
                                          )}
                                      </Box>
                                  </Box>
                              </Grid>
                              
                              <Grid item xs={12} md={2} sx={{ textAlign: 'center' }}>
                                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' }, mx: 'auto', height: 50 }} />
                                  <Divider sx={{ display: { xs: 'block', md: 'none' }, my: 2 }} />
                              </Grid>

                              <Grid item xs={12} md={5}>
                                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                                      <Typography variant="caption" color="text.secondary" fontWeight="bold">TOTAL SPENT</Typography>
                                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 1 }}>
                                          <Typography variant="h4" fontWeight="bold" color="text.primary">$</Typography>
                                          {editMode ? (
                                              <TextField variant="standard" type="number" name="spent" value={editData.spent} onChange={handleInputChange} sx={{ width: 100, input: { textAlign: 'center', fontSize: '1.5rem' } }} />
                                          ) : (
                                              <Typography variant="h4" fontWeight="bold" color="text.primary">{project.spent?.toLocaleString()}</Typography>
                                          )}
                                      </Box>
                                  </Box>
                              </Grid>
                          </Grid>
                          
                          <Box sx={{ mt: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="caption">Budget Utilization</Typography>
                                  <Typography variant="caption">{Math.round((project.spent / project.budget) * 100) || 0}%</Typography>
                              </Box>
                              <LinearProgress 
                                  variant="determinate" 
                                  value={Math.min((project.spent / project.budget) * 100, 100)} 
                                  sx={{ 
                                      height: 10, 
                                      borderRadius: 5, 
                                      bgcolor: 'grey.200',
                                      '& .MuiLinearProgress-bar': { 
                                          borderRadius: 5,
                                          bgcolor: project.spent > project.budget ? 'error.main' : 'success.main'
                                      } 
                                  }} 
                              />
                          </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Location Map */}
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <MapIcon color="action" />
                            <Typography variant="h6" fontWeight="medium">Project Location</Typography>
                        </Box>
                        {editMode && (
                          <Button 
                            size="small" 
                            variant="outlined" 
                            startIcon={<MyLocationIcon />} 
                            onClick={getLocation}
                          >
                            Use My Location
                          </Button>
                        )}
                      </Box>
                      
                      {editMode ? (
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Latitude"
                              name="latitude"
                              type="number"
                              value={editData.latitude || ''}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Longitude"
                              name="longitude"
                              type="number"
                              value={editData.longitude || ''}
                              onChange={handleInputChange}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      ) : (
                        project.latitude && project.longitude ? (
                          <Box sx={{ width: '100%', height: 400, borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider', boxShadow: 1 }}>
                            <MapContainer center={[project.latitude, project.longitude]} zoom={13} style={{ height: '100%', width: '100%' }}>
                                <TileLayer
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                                />
                                <Marker position={[project.latitude, project.longitude]} />
                            </MapContainer>
                          </Box>
                        ) : (
                          <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2 }}>
                            <PublicIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                No location data set for this project.
                            </Typography>
                          </Box>
                        )
                      )}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </TabPanel>

            {/* Team Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Team Members</Typography>
                <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)} sx={{ borderRadius: 2 }}>
                  Add Member
                </Button>
              </Box>
              <Grid container spacing={3}>
                {team.map((member) => (
                  <Grid item xs={12} sm={6} md={4} key={member.user_id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 3,
                        cursor: 'pointer',
                        transition: 'transform 0.2s, box-shadow 0.2s',
                        '&:hover': { transform: 'translateY(-2px)', boxShadow: 3, borderColor: 'primary.main' }
                      }}
                      onClick={() => handleMemberClick(member)}
                    >
                      <CardContent sx={{ position: 'relative' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', width: 48, height: 48 }}>{member.name[0]}</Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="bold" noWrap>{member.name}</Typography>
                                <Chip label={member.role} size="small" color="primary" variant="filled" sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold' }} />
                            </Box>
                        </Box>
                        
                        <Divider sx={{ mb: 2 }} />
                        
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                                <AccessTimeIcon fontSize="small" />
                                <Typography variant="body2">{member.total_hours || 0} Hours Logged</Typography>
                            </Box>
                            <IconButton 
                                size="small" 
                                color="error" 
                                onClick={(e) => { 
                                e.stopPropagation(); 
                                handleRemoveUser(member.user_id); 
                                }} 
                                sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'error.main', color: 'white' } }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                {team.length === 0 && (
                  <Grid item xs={12}>
                    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                      <PersonAddIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary" gutterBottom>No team members yet</Typography>
                      <Button variant="outlined" onClick={() => setOpenAssignDialog(true)}>Assign Someone</Button>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Reports Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Project Reports</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenReportDialog(true)} sx={{ borderRadius: 2 }}>
                  New Report Folder
                </Button>
              </Box>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {project.reports && project.reports.map((report) => (
                <Accordion 
                    key={report.id} 
                    disableGutters 
                    elevation={0} 
                    sx={{ 
                        border: '1px solid', 
                        borderColor: 'divider', 
                        borderRadius: '12px !important', 
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                        mb: 1
                    }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: 'white', '&:hover': { bgcolor: 'grey.50' } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', pr: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'warning.light', color: 'warning.dark' }}>
                            <FolderIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle1" fontWeight="bold">{report.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{report.subreports?.length || 0} Revisions</Typography>
                        </Box>
                      </Box>
                      <Chip label={report.created_at} size="small" variant="outlined" />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', bgcolor: 'grey.50', p: 0 }}>
                    <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white' }}>
                      <Button 
                        size="small" 
                        variant="contained" 
                        color="primary"
                        startIcon={addingRevisionId === report.id ? <CircularProgress size={20} color="inherit" /> : <AddIcon />} 
                        onClick={() => handleAddSubReport(report.id)}
                        disabled={addingRevisionId === report.id}
                      >
                        {addingRevisionId === report.id ? 'Creating...' : 'Create New Revision'}
                      </Button>
                    </Box>
                    <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 600 }}>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Document ID</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Revision</TableCell>
                          <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Status</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {report.subreports.map(sub => (
                          <TableRow key={sub.id} hover sx={{ bgcolor: 'white' }}>
                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>{sub.generated_id}</TableCell>
                            <TableCell>
                              <Chip label={`Rev ${sub.revision}`} size="small" color="secondary" sx={{ borderRadius: 1, fontWeight: 'bold' }} />
                            </TableCell>
                            <TableCell>
                                {sub.has_file ? 
                                    <Chip icon={<CheckCircleIcon />} label="Uploaded" color="success" size="small" variant="outlined" /> : 
                                    <Chip label="Pending File" color="warning" size="small" variant="outlined" />
                                }
                            </TableCell>
                            <TableCell align="right">
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                <Tooltip title="View QR">
                                  <IconButton size="small" onClick={() => handleViewQR(`${API_URL}/subreports/${sub.generated_id}/qr`, sub.generated_id)} color="primary">
                                    <QrCodeIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Upload PDF">
                                  <span>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleUploadClick(sub.generated_id)} 
                                      color="info"
                                      disabled={uploadingSubReportId === sub.generated_id}
                                    >
                                      {uploadingSubReportId === sub.generated_id ? <CircularProgress size={20} /> : <CloudUploadIcon fontSize="small" />}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Download PDF">
                                  <span>
                                    <IconButton size="small" disabled={!sub.has_file} onClick={() => handleDownloadSubReport(sub.generated_id)} color="success">
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {report.subreports.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                <DescriptionIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                              <Typography variant="body2">No revisions created yet.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
              </Box>
              
              {(!project.reports || project.reports.length === 0) && (
                <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                  <FolderIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>No reports created yet</Typography>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenReportDialog(true)}>Create First Report</Button>
                </Paper>
              )}
            </TabPanel>

            {/* Documents Tab */}
            <TabPanel value={tabValue} index={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Project Documents</Typography>
                <Button 
                    variant="contained" 
                    startIcon={uploadingFile ? <CircularProgress size={20} color="inherit" /> : <CloudUploadIcon />} 
                    component="label"
                    sx={{ borderRadius: 2 }}
                    disabled={uploadingFile}
                >
                  Upload Document
                  <input type="file" hidden onChange={handleFileUpload} />
                </Button>
              </Box>

              <Grid container spacing={3}>
                {documents.map((doc, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card 
                        variant="outlined" 
                        sx={{ 
                            borderRadius: 3, 
                            display: 'flex', 
                            flexDirection: 'column',
                            height: '100%',
                            transition: 'all 0.2s',
                            '&:hover': { transform: 'translateY(-4px)', boxShadow: 4, borderColor: 'primary.main' }
                        }}
                    >
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 4 }}>
                        <Avatar sx={{ bgcolor: 'info.light', color: 'info.main', width: 64, height: 64, mb: 2 }}>
                            <InsertDriveFileIcon fontSize="large" />
                        </Avatar>
                        <Typography variant="subtitle1" fontWeight="bold" noWrap sx={{ width: '100%', mb: 1 }} title={doc}>
                            {doc}
                        </Typography>
                        <Chip label="Document" size="small" variant="outlined" sx={{ mb: 2 }} />
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ justifyContent: 'center', bgcolor: 'grey.50', px: 2 }}>
                        <Button 
                            size="small" 
                            startIcon={<DownloadIcon />} 
                            onClick={() => handleDownloadDocument(doc)}
                            fullWidth
                        >
                            Download
                        </Button>
                      </CardActions>
                    </Card>
                  </Grid>
                ))}
                
                {/* Empty State / Dropzone placeholder */}
                {documents.length === 0 && (
                  <Grid item xs={12}>
                    <Paper 
                        variant="outlined" 
                        sx={{ 
                            p: 8, 
                            textAlign: 'center', 
                            bgcolor: 'grey.50', 
                            borderRadius: 4, 
                            borderStyle: 'dashed', 
                            borderWidth: 2,
                            borderColor: 'divider',
                            cursor: 'pointer',
                            '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.50' }
                        }}
                        component="label"
                    >
                      <CloudUploadIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h5" color="textSecondary" gutterBottom>No documents found</Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                        Upload specifications, contracts, or other project files here.
                      </Typography>
                      <Button variant="contained" component="span" startIcon={<CloudUploadIcon />}>
                        Select File
                      </Button>
                      <input type="file" hidden onChange={handleFileUpload} />
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </TabPanel>

            {/* Milestones Tab */}
            <TabPanel value={tabValue} index={4}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" fontWeight="bold">Project Milestones</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)} sx={{ borderRadius: 2 }}>
                  Add Milestone
                </Button>
              </Box>

              <Box sx={{ position: 'relative' }}>
                {milestones && milestones.length > 0 && (
                    <Box 
                        sx={{ 
                            position: 'absolute', 
                            left: 20, 
                            top: 20, 
                            bottom: 20, 
                            width: 2, 
                            bgcolor: 'divider', 
                            zIndex: 0,
                            display: { xs: 'none', md: 'block' }
                        }} 
                    />
                )}
                
                <Grid container spacing={3}>
                    {milestones && milestones.map((milestone, index) => (
                    <Grid item xs={12} key={milestone.id}>
                        <Card 
                            sx={{ 
                                display: 'flex', 
                                flexDirection: { xs: 'column', md: 'row' },
                                alignItems: { md: 'center' },
                                p: 2,
                                borderRadius: 3,
                                borderLeft: milestone.completed ? '6px solid #2e7d32' : '6px solid #1976d2',
                                ml: { md: 6 },
                                position: 'relative',
                                overflow: 'visible'
                            }}
                            elevation={2}
                        >
                            {/* Timeline Dot */}
                            <Box 
                                sx={{ 
                                    position: 'absolute', 
                                    left: -34, 
                                    top: '50%', 
                                    transform: 'translateY(-50%)',
                                    width: 16, 
                                    height: 16, 
                                    borderRadius: '50%', 
                                    bgcolor: milestone.completed ? 'success.main' : 'primary.main',
                                    border: '4px solid white',
                                    boxShadow: 1,
                                    zIndex: 1,
                                    display: { xs: 'none', md: 'block' }
                                }} 
                            />

                            <Box sx={{ flexGrow: 1, ml: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                    <Typography variant="h6" fontWeight="bold">{milestone.name}</Typography>
                                    <Chip 
                                        label={milestone.completed ? 'Completed' : 'In Progress'} 
                                        color={milestone.completed ? "success" : "primary"} 
                                        size="small" 
                                        icon={milestone.completed ? <CheckCircleIcon /> : <AccessTimeIcon />}
                                    />
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {milestone.description || "No description provided."}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                        <CalendarTodayIcon fontSize="small" />
                                        <Typography variant="caption" fontWeight="medium">
                                            Due: {milestone.due_date ? new Date(milestone.due_date).toLocaleDateString() : 'No date'}
                                        </Typography>
                                    </Box>
                                    {!milestone.completed && (
                                        <Box sx={{ width: 150 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">Progress</Typography>
                                                <Typography variant="caption" fontWeight="bold">50%</Typography>
                                            </Box>
                                            <LinearProgress variant="determinate" value={50} sx={{ height: 6, borderRadius: 3 }} />
                                        </Box>
                                    )}
                                </Box>
                            </Box>
                            
                            <Divider orientation="vertical" flexItem sx={{ mx: 2, display: { xs: 'none', md: 'block' } }} />
                            <Divider sx={{ my: 2, display: { xs: 'block', md: 'none' } }} />

                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', minWidth: 100 }}>
                                <Tooltip title="Mark Complete">
                                    <IconButton 
                                        color="success" 
                                        disabled={milestone.completed}
                                        onClick={() => handleUpdateMilestoneStatus(milestone.id, 'Completed')}
                                        sx={{ bgcolor: 'success.50' }}
                                    >
                                        <CheckCircleIcon />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Milestone">
                                    <IconButton 
                                        color="error" 
                                        onClick={() => handleDeleteMilestone(milestone.id)}
                                        sx={{ bgcolor: 'error.50' }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Card>
                    </Grid>
                    ))}
                </Grid>
                
                {(!milestones || milestones.length === 0) && (
                    <Paper variant="outlined" sx={{ p: 6, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, borderStyle: 'dashed' }}>
                      <FlagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                      <Typography variant="h6" color="textSecondary" gutterBottom>No milestones set</Typography>
                      <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setOpenMilestoneDialog(true)}>Add First Milestone</Button>
                    </Paper>
                )}
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Dialogs */}
      <Dialog open={openReportDialog} onClose={() => setOpenReportDialog(false)}>
        <DialogTitle>Add New Report</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Report Name" fullWidth
            value={newReportName} onChange={(e) => setNewReportName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReportDialog(false)}>Cancel</Button>
          <Button onClick={handleAddReport} variant="contained" disabled={createReportLoading}>
            {createReportLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
        <DialogTitle>Assign User</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <Autocomplete
            fullWidth
            margin="dense"
            options={allUsers}
            getOptionLabel={(option) => `${option.full_name} (${option.role})`}
            filterOptions={(options, { inputValue }) => {
                return options.filter(option =>
                    option.username.toLowerCase().includes(inputValue.toLowerCase()) ||
                    (option.full_name && option.full_name.toLowerCase().includes(inputValue.toLowerCase()))
                );
            }}
            value={allUsers.find(u => u.id === assignUser) || null}
            onChange={(event, newValue) => {
                setAssignUser(newValue ? newValue.id : '');
            }}
            renderInput={(params) => <TextField {...params} label="User" placeholder="Search user..." />}
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Role</InputLabel>
            <Select value={assignRole} onChange={(e) => setAssignRole(e.target.value)} label="Role">
              <MenuItem value="Manager">Manager</MenuItem>
              <MenuItem value="Lead">Lead</MenuItem>
              <MenuItem value="Member">Member</MenuItem>
              <MenuItem value="Viewer">Viewer</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained" disabled={assignLoading}>
            {assignLoading ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openMilestoneDialog} onClose={() => setOpenMilestoneDialog(false)}>
        <DialogTitle>Add Milestone</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus margin="dense" label="Milestone Name" fullWidth
            value={newMilestoneName} onChange={(e) => setNewMilestoneName(e.target.value)}
          />
          <TextField
            margin="dense" label="Due Date" type="date" fullWidth InputLabelProps={{ shrink: true }}
            value={newMilestoneDate} onChange={(e) => setNewMilestoneDate(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenMilestoneDialog(false)}>Cancel</Button>
          <Button onClick={handleAddMilestone} variant="contained">Create</Button>
        </DialogActions>
      </Dialog>

      {/* Member Hours Dialog */}
      <Dialog open={openMemberDialog} onClose={() => setOpenMemberDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
            {selectedMember?.name} - Work Log
        </DialogTitle>
        <DialogContent>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Duration (Hrs)</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {memberHours.map((log) => (
                        <TableRow key={log.id}>
                            <TableCell>{log.date}</TableCell>
                            <TableCell>{log.start_time} - {log.end_time}</TableCell>
                            <TableCell>{log.duration}</TableCell>
                            <TableCell>{log.type_of_work}</TableCell>
                            <TableCell>{log.description}</TableCell>
                        </TableRow>
                    ))}
                    {memberHours.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={5} align="center">No work logs found.</TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setOpenMemberDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={qrDialog.open} onClose={() => setQrDialog({ ...qrDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center' }}>{qrDialog.title}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <img 
              src={qrDialog.url} 
              alt="QR Code" 
              style={{ maxWidth: '100%', height: 'auto', borderRadius: 8, border: '1px solid #eee' }} 
              onError={(e) => {e.target.onerror = null; e.target.src="https://via.placeholder.com/150?text=No+QR"}}
            />
          </Box>
          <Typography variant="caption" display="block" align="center" color="textSecondary">
            Scan to view details
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button onClick={() => setQrDialog({ ...qrDialog, open: false })} variant="outlined">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input for subreport uploads */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="application/pdf"
        onChange={handleSubReportFileChange}
      />

    </Container>
  );
}

export default ProjectDetails;

